import { useState, useEffect, useCallback } from 'react';
import PaymentSuccess from './PaymentSuccess';
import BillingDashboard from './BillingDashboard';
import { AlertCircle } from 'lucide-react';
import api from '../../utils/api';

const envApiUrl = process.env.REACT_APP_API_BASE_URL || '';
const SERVER_URL = envApiUrl ? envApiUrl.replace(/\/api$/, '') : '';

const PLAN_PRESETS = {
  free_trial:   { maxAiAgents: 1, maxHumanAgents: 1, maxUsers: 1, creditsTotal: 400000, price: 0 },
  starter:      { maxAiAgents: 3, maxHumanAgents: 3, maxUsers: 3, creditsTotal: 2000000, price: 999 },
  growth:       { maxAiAgents: 5, maxHumanAgents: 5, maxUsers: 5, creditsTotal: 10000000, price: 2499 },
  professional: { maxAiAgents: 7, maxHumanAgents: 7, maxUsers: 7, creditsTotal: 30000000, price: 4999 },
  enterprise:   { maxAiAgents: 999, maxHumanAgents: 999, maxUsers: 999, creditsTotal: 999999, price: 9999 },
};

async function loadRazorpaySDK() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src     = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

// The server prices the order (plan, cycle, add-ons, GST) and later grants exactly what was paid for.
// The browser only says what the customer wants and shows the server's amount.
async function launchRazorpay({ planName, userName, userEmail, planId, SERVER_URL, onSuccess, onFail, onDismiss, billingCycle = 'monthly', purchaseType = 'plan', extraAiAgents = 0, extraHumanAgents = 0, extraCredits = null }) {
  const loaded = await loadRazorpaySDK();
  if (!loaded || !window.Razorpay) { onFail('Razorpay SDK failed to load'); return; }

  let orderId = null, keyId = null, amountPaise = 0, total = 0, orderName = planName;
  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${api.getAuthToken()}` };

  const reportFailure = async (errorMessage) => {
    onFail(errorMessage);
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    const res = await fetch(`${SERVER_URL}/api/payment/create-order`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ planId, billingCycle, purchaseType, extraAiAgents, extraHumanAgents, extraCredits }),
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    const d = await res.json();
    if (!res.ok) throw new Error(d.detail || d.error || 'Order creation failed');
    orderId = d.orderId; keyId = d.keyId; amountPaise = d.amount; total = d.total; orderName = d.planName || planName;
  } catch (e) {
    clearTimeout(timeoutId);
    reportFailure(e.name === 'AbortError' ? 'Request timed out. Please check your connection.' : e.message);
    return;
  }

  const rzp = new window.Razorpay({
    key: keyId, amount: amountPaise, currency: 'INR',
    name: 'EasyChat', description: orderName, order_id: orderId,
    prefill: { 
      name: userName, 
      email: userEmail || 'customer@example.com' 
    },
    theme: { color: '#82bbe0' },
    handler: async (response) => {
      const verifyController = new AbortController();
      const verifyTimeoutId = setTimeout(() => verifyController.abort(), 15000);
      try {
        const vres = await fetch(`${SERVER_URL}/api/payment/verify`, {
          method: 'POST', headers: authHeaders,
          body: JSON.stringify({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          }),
          signal: verifyController.signal
        });
        clearTimeout(verifyTimeoutId);
        const vd = await vres.json();
        if (vd.success) onSuccess({ paymentId: response.razorpay_payment_id, amount: total });
        else reportFailure('Payment verification failed. Contact support.');
      } catch (e) {
        clearTimeout(verifyTimeoutId);
        reportFailure(e.name === 'AbortError' ? 'Verification timed out. Contact support.' : 'Verification error. Contact support.');
      }
    },
    modal: { ondismiss: onDismiss },
  });
  rzp.on('payment.failed', (r) => reportFailure(r.error?.description || 'Payment failed'));
  rzp.open();
}

export default function PaymentPage({ userName, tenantId }) {
  const [loadingId, setLoadingId]           = useState('');
  const [error, setError]                   = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [subscription, setSubscription]     = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [userEmail, setUserEmail]           = useState('');

  useEffect(() => {
    try {
      const userStr = sessionStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : null;
      if (userObj && userObj.email) {
        setUserEmail(userObj.email);
      }
    } catch (e) {
      console.error("Failed to load user email from session:", e);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    const userStr = sessionStorage.getItem('user');
    const userObj = userStr ? JSON.parse(userStr) : null;
    const resolvedTenantId = tenantId || userObj?.tenant_id;
    if (!resolvedTenantId) {
      setPaymentHistory([]);
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const json = await api.get(`/invoices/${resolvedTenantId}`);
      if (json.success && Array.isArray(json.data)) {
        const dbHistory = json.data.map(inv => {
          const isTopup = inv.plan_name?.toLowerCase().includes('topup') || inv.plan_name?.toLowerCase().includes('top-up') || inv.plan_name?.toLowerCase().includes('credit');
          
          const addOns = { extraAiAgents: 0, extraHumanAgents: 0, extraCredits: null };
          const planNameLower = inv.plan_name?.toLowerCase() || '';
          
          if (planNameLower.includes('extra ai agent')) {
            const match = planNameLower.match(/(\d+)\s*(?:x\s*)?extra ai agent/);
            if (match) addOns.extraAiAgents = parseInt(match[1], 10);
          }
          if (planNameLower.includes('extra human agent')) {
            const match = planNameLower.match(/(\d+)\s*(?:x\s*)?extra human agent/);
            if (match) addOns.extraHumanAgents = parseInt(match[1], 10);
          }
          if (planNameLower.includes('credit pack')) {
            const match = planNameLower.match(/credit pack\s*\(([^)]+)\)/);
            if (match) addOns.extraCredits = { k: match[1], price: 0 };
          }

          const hasAddOns = addOns.extraAiAgents > 0 || addOns.extraHumanAgents > 0 || !!addOns.extraCredits;

          return {
            paymentId: inv.transaction_id,
            planName: inv.plan_name,
            planType: isTopup ? 'topup' : 'subscription',
            amount: Number(inv.amount),
            paidAt: inv.created_at || inv.invoice_date,
            validUntil: null,
            addOns: hasAddOns ? addOns : null,
          };
        });
        setPaymentHistory(dbHistory);
      } else {
        setPaymentHistory([]);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      console.error("Failed to load invoices from DB:", err);
      setPaymentHistory([]);
    }
  }, [tenantId]);

  const loadSubscription = useCallback(async () => {
    const userStr = sessionStorage.getItem('user');
    const userObj = userStr ? JSON.parse(userStr) : null;
    const resolvedTenantId = tenantId || userObj?.tenant_id;
    if (!resolvedTenantId) {
      setSubscription({ planName: 'No Plan', planId: null, planPrice: 0, renewsAt: null, maxAiAgents: 0, maxHumanAgents: 0, maxUsers: 0, maxAgents: 0, creditsUsed: 0, creditsTotal: 0, topupCredits: 0, service_active: false });
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const json = await api.get(`/subscription/${resolvedTenantId}`);

      if (json.success && json.data) {
        const sub = json.data;

        const key    = sub.plan_id ? sub.plan_id.toLowerCase() : '';
        const preset = PLAN_PRESETS[key] || { maxAiAgents: 0, maxHumanAgents: 0, maxUsers: 0, creditsTotal: 0, price: 0 };
        const isAnnual = sub.plan_type === 'Yearly';
        const basePrice = preset.price || 0;
        const planPrice = isAnnual ? Math.round(basePrice * 12 * 0.8) : basePrice;

        const mapped = {
          planName:          sub.plan_name,
          planId:            sub.plan_id,
          planPrice:         planPrice,
          renewsAt:          sub.next_billing,
          maxAiAgents:       preset.maxAiAgents + (sub.extra_ai_agents || 0),
          maxHumanAgents:    preset.maxHumanAgents + (sub.extra_human_agents || 0),
          maxUsers:          preset.maxUsers,
          maxAgents:         preset.maxAiAgents + (sub.extra_ai_agents || 0),
          creditsUsed:       0,
          creditsTotal:      preset.creditsTotal,
          topupCredits:      0,
          topupCreditsTotal: 0,
          service_active:    sub.status === 'ACTIVE',
        };

        const creditController = new AbortController();
        const creditTimeoutId = setTimeout(() => creditController.abort(), 10000);
        try {
          const cjson = await api.get(`/credits/${resolvedTenantId || userName}`);
          if (cjson.success && cjson.data) {
            mapped.creditsUsed       = cjson.data.credits_used         ?? 0;
            mapped.creditsTotal      = cjson.data.monthly_credits       ?? 0;
            mapped.topupCredits      = cjson.data.topup_credits         ?? 0;
            mapped.topupCreditsTotal = cjson.data.topup_credits_total   ?? 0;
          }
        } catch (ce) {
          clearTimeout(creditTimeoutId);
          console.error("Failed to load credits for subscription mapping:", ce);
        }

        setSubscription(mapped);
      } else {
        setSubscription({ planName: 'No Plan', planId: null, planPrice: 0, renewsAt: null, maxAiAgents: 0, maxHumanAgents: 0, maxUsers: 0, maxAgents: 0, creditsUsed: 0, creditsTotal: 0, topupCredits: 0, service_active: false });
      }
    } catch (err) {
      clearTimeout(timeoutId);
      console.error("Failed to load subscription details:", err);
      setSubscription({ planName: 'No Plan', planId: null, planPrice: 0, renewsAt: null, maxAiAgents: 0, maxHumanAgents: 0, maxUsers: 0, maxAgents: 0, creditsUsed: 0, creditsTotal: 0, topupCredits: 0, service_active: false });
    }
  }, [userName, tenantId]);

  useEffect(() => {
    fetchInvoices();
    loadSubscription();
  }, [fetchInvoices, loadSubscription]);

  const handleSubscriptionChange = (updated) => {
    setSubscription(updated);
  };

  const handleSubscriptionPay = async (planKey, planLabel, basePrice, billingCycle = 'monthly', addOns = null) => {
    if (loadingId) return;
    const lid = 'plan_' + planKey;
    setLoadingId(lid); setError('');

    const isYearly   = billingCycle === 'yearly';
    const planLabelWithCycle = isYearly ? `${planLabel} (Yearly)` : planLabel;

    const extraAiAgents = addOns?.extraAiAgents || 0;
    const extraHumanAgents = addOns?.extraHumanAgents || 0;
    const extraCredits = addOns?.extraCredits ? { k: addOns.extraCredits.k, label: addOns.extraCredits.label, price: addOns.extraCredits.price } : null;
    // add-ons for the plan the workspace already has: the plan itself is not bought again
    const purchaseType = addOns?.addonOnly ? 'addon' : 'plan';

    await launchRazorpay({
      planName: planLabelWithCycle, userName, userEmail,
      planId: planKey, SERVER_URL, billingCycle, purchaseType,
      extraAiAgents, extraHumanAgents, extraCredits,
      onSuccess: async ({ paymentId, amount: total }) => {
        await loadSubscription();
        await fetchInvoices();
        setPaymentSuccess({ 
          paymentId, 
          plan: { name: planLabelWithCycle, category: 'subscription' }, 
          amount: total, 
          addOns: { extraAiAgents, extraHumanAgents, extraCredits } 
        });
        setLoadingId('');
        window.dispatchEvent(new Event("credits_updated"));
      },
      onFail:    (msg) => { setError(msg); setLoadingId(''); },
      onDismiss: () => setLoadingId(''),
    });
  };

  const handleCreditPay = async (pack) => {
    if (loadingId) return;
    const lid = 'topup_' + pack.k;
    setLoadingId(lid); setError('');
    const topupPlanId = 'topup_' + pack.k;
    const topupPlanName = `Top-up: ${pack.label || pack.k} credits`;

    await launchRazorpay({
      planName: topupPlanName, userName, userEmail,
      planId: topupPlanId, SERVER_URL, purchaseType: 'topup',
      onSuccess: async ({ paymentId, amount: total }) => {
        await loadSubscription();
        await fetchInvoices();
        setPaymentSuccess({ paymentId, plan: { name: topupPlanName, category: 'topup' }, amount: total });
        setLoadingId('');
        window.dispatchEvent(new Event("credits_updated"));
      },
      onFail:    (msg) => { setError(msg); setLoadingId(''); },
      onDismiss: () => setLoadingId(''),
    });
  };

  if (paymentSuccess) {
    return (
      <PaymentSuccess
        paymentId={paymentSuccess.paymentId}
        plan={paymentSuccess.plan}
        amount={paymentSuccess.amount}
        userName={userName}
        addOns={paymentSuccess.addOns}
        onDone={() => setPaymentSuccess(null)}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif]">
      <div className="w-full max-w-7xl mx-auto px-3.5 py-4 sm:px-6 sm:py-6 lg:px-8 space-y-6 sm:space-y-8 min-w-0">
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-xs sm:text-sm max-w-4xl mx-auto shadow-sm">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span className="font-medium flex-1 break-words">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600 font-bold p-1">✕</button>
          </div>
        )}

        <BillingDashboard
          userName={userName}
          subscription={subscription}
          onSubscriptionChange={handleSubscriptionChange}
          onSubscriptionPay={handleSubscriptionPay}
          onCreditPay={handleCreditPay}
          loadingId={loadingId}
          paymentHistory={paymentHistory}
          serverUrl={SERVER_URL}
        />
      </div>
    </div>
  );
}